import { useEffect, useMemo, useRef, useState } from "react";
import { X, Check, AlertTriangle, RotateCcw } from "lucide-react";

const WIN_W = 320;

/**
 * Value-checking window for the calculator tape.
 *
 * Every figure that went into a calculation gets its own square card, and the
 * cursor walks them with the arrow keys — one number at a time, with the
 * running subtotal beside it — so a long entry can be read back against the
 * paper voucher without losing your place. Each card is marked OK or FLAGGED
 * as you go, and the header counts how far through you are.
 *
 * Presentation only: every card's text is formatted by the calculator, so this
 * file needs none of its maths.
 */
export default function CalculatorTapeWindow({ open, onClose, items, anchor }) {
  const [cursor, setCursor] = useState(0);
  const [marks, setMarks] = useState({}); // id -> "ok" | "bad"
  const listRef = useRef(null);
  const winRef = useRef(null);

  const total = items.length;
  const checked = useMemo(
    () => items.filter((it) => marks[it.id]).length,
    [items, marks]
  );
  const flagged = useMemo(
    () => items.filter((it) => marks[it.id] === "bad").length,
    [items, marks]
  );

  // Keep the cursor on a real card as calculations come and go.
  useEffect(() => {
    setCursor((c) => Math.min(Math.max(0, c), Math.max(0, total - 1)));
  }, [total]);

  useEffect(() => {
    if (open) winRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Follow the cursor without yanking the whole page around.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-idx="${cursor}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [cursor, open, total]);

  // Window-level so the arrows work straight after clicking expand, without
  // having to click the card grid first. Skipped while a real field is focused
  // (GST / Tools inputs stay typable with the checker open).
  useEffect(() => {
    if (!open) return;
    const mark = (kind) =>
      setMarks((m) => {
        const it = items[cursor];
        if (!it) return m;
        const next = { ...m };
        if (next[it.id] === kind) delete next[it.id];
        else next[it.id] = kind;
        return next;
      });

    const onKey = (e) => {
      const t = e.target;
      if (t && (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const last = Math.max(0, items.length - 1);
      const step = (d) => setCursor((c) => Math.min(Math.max(0, c + d), last));

      switch (e.key) {
        case "ArrowDown":
        case "ArrowRight": step(1); break;
        case "ArrowUp":
        case "ArrowLeft": step(-1); break;
        case "Home": setCursor(0); break;
        case "End": setCursor(last); break;
        case "Enter":
        case " ":
          mark("ok");
          setCursor((c) => Math.min(c + 1, last));   // auto-advance = progress
          break;
        case "x":
        case "X":
        case "Backspace":
        case "Delete":
          mark("bad");
          setCursor((c) => Math.min(c + 1, last));
          break;
        case "Escape": onClose(); break;
        default: return;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, items, cursor, onClose]);

  if (!open) return null;

  // Sits beside the calculator — left if there is room, otherwise right.
  const left = Math.min(
    Math.max(4, anchor.x - WIN_W - 8 >= 4 ? anchor.x - WIN_W - 8 : anchor.x + anchor.w + 8),
    Math.max(4, window.innerWidth - WIN_W - 4)
  );
  const top = Math.min(Math.max(4, anchor.y), Math.max(4, window.innerHeight - 200));

  // One figure per row, ruled off from the next — read straight down the
  // column the way you would read a printed tape.
  const rowCls = (it, i) => {
    const state = marks[it.id];
    const base =
      "w-full flex items-center gap-2 px-2 py-2 border-l-2 text-left transition-colors select-none";
    const tone =
      state === "ok"
        ? "bg-green-500/10 border-l-green-500"
        : state === "bad"
        ? "bg-red-500/10 border-l-red-500"
        : it.result
        ? "bg-muted/40 border-l-transparent"   // closes off one calculation
        : "border-l-transparent";
    const focus =
      i === cursor
        ? "ring-1 ring-inset ring-primary border-l-primary shadow-sm"
        : "hover:bg-muted/50";
    return `${base} ${tone} ${focus}`;
  };

  return (
    <div
      ref={winRef}
      tabIndex={-1}
      data-testid="calculator-check-window"
      style={{ left, top, width: WIN_W }}
      className="fixed z-[201] rounded-xl border border-border bg-card shadow-2xl focus:outline-none"
    >
      <div className="flex items-center gap-1.5 px-2.5 h-9 border-b border-border">
        <span className="text-xs font-semibold tracking-tight flex-1">Check values</span>
        {Object.keys(marks).length > 0 && (
          <button
            type="button"
            onClick={() => setMarks({})}
            title="Reset progress"
            className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close"
          data-testid="calculator-check-close"
          className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* progress */}
      <div className="px-2.5 py-1.5 border-b border-border/60">
        <div className="flex items-center justify-between text-[10px] font-medium">
          <span className="text-muted-foreground tabular-nums">
            {checked} / {total} checked
          </span>
          {flagged > 0 && (
            <span className="text-red-600 tabular-nums">{flagged} flagged</span>
          )}
        </div>
        <div className="mt-1 h-1 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${total ? (checked / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-2">
        {total === 0 ? (
          <div className="py-8 text-center text-[11px] text-muted-foreground">
            Nothing to check yet — do a calculation first
          </div>
        ) : (
          <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
            {items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                data-idx={i}
                data-testid={`check-card-${i}`}
                onClick={() => setCursor(i)}
                onDoubleClick={() =>
                  setMarks((m) => ({ ...m, [it.id]: m[it.id] === "ok" ? undefined : "ok" }))
                }
                className={rowCls(it, i)}
              >
                {/* operator gutter */}
                <span className="w-4 shrink-0 text-center text-sm font-mono text-muted-foreground">
                  {it.sym}
                </span>

                {/* the figure — the thing being checked, so it gets the size */}
                <span
                  className={`flex-1 text-right font-mono tabular-nums truncate ${
                    it.result ? "text-lg font-semibold" : "text-base"
                  } ${it.live ? "text-muted-foreground" : ""}`}
                >
                  {it.text}
                </span>

                {/* running subtotal + mark */}
                <span className="w-16 shrink-0 text-right text-[10px] font-mono tabular-nums text-muted-foreground/70 truncate">
                  {it.runText}
                </span>
                <span className="w-3.5 shrink-0 flex items-center justify-center">
                  {marks[it.id] === "ok" && <Check className="w-3.5 h-3.5 text-green-600" />}
                  {marks[it.id] === "bad" && <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-2.5 py-1.5 border-t border-border text-[9px] text-muted-foreground flex items-center gap-2 flex-wrap">
        <span>↑ ↓ ← → move</span>
        <span>·</span>
        <span>Enter = correct</span>
        <span>·</span>
        <span>X = flag</span>
        <span>·</span>
        <span>Esc close</span>
      </div>
    </div>
  );
}
